import { useEffect, useState, useCallback } from "react";
import { getMyProfile, updateMyProfile } from "@/api/profilo";
import type { ProfileDTO, ProfileAddress } from "@/api/profilo";
import { PraticheSkeleton } from "@/components/PraticheSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { ApiError } from "@/api/client";

interface FormState {
  phoneNumber: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  interno: string;
}

function profileToForm(p: ProfileDTO): FormState {
  return {
    phoneNumber: p.phoneNumber ?? "",
    street: p.address?.street ?? "",
    city: p.address?.city ?? "",
    state: p.address?.state ?? "",
    postalCode: p.address?.postalCode ?? "",
    country: p.address?.country ?? "",
    interno: p.address?.interno ?? "",
  };
}

function isDirty(form: FormState, original: FormState): boolean {
  return (Object.keys(form) as (keyof FormState)[]).some((k) => form[k] !== original[k]);
}

export function Profilo() {
  const [profilo, setProfilo] = useState<ProfileDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({ phoneNumber: "", street: "", city: "", state: "", postalCode: "", country: "", interno: "" });
  const [original, setOriginal] = useState<FormState>({ phoneNumber: "", street: "", city: "", state: "", postalCode: "", country: "", interno: "" });
  const [saving, setSaving] = useState(false);
  const [successBanner, setSuccessBanner] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getMyProfile()
      .then((data) => {
        setProfilo(data);
        const f = profileToForm(data);
        setForm(f);
        setOriginal(f);
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          setError("Sessione scaduta. Ricarica la pagina.");
        } else {
          setError("Impossibile caricare il profilo.");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  function updateField(key: keyof FormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);
    const dirty = isDirty(form, original);
    if (!dirty) { setSaving(false); return; }

    const payload: Parameters<typeof updateMyProfile>[0] = {};
    if (form.phoneNumber !== original.phoneNumber) {
      payload.phoneNumber = form.phoneNumber;
    }
    const addrKeys: (keyof ProfileAddress)[] = ["street", "city", "state", "postalCode", "country", "interno"];
    const addrChanged = addrKeys.some((k) => form[k] !== original[k]);
    if (addrChanged) {
      payload.address = {};
      for (const k of addrKeys) {
        if (form[k] !== original[k]) {
          (payload.address as Record<string, string>)[k] = form[k];
        }
      }
    }

    try {
      const updated = await updateMyProfile(payload);
      setProfilo(updated);
      const f = profileToForm(updated);
      setForm(f);
      setOriginal(f);
      setSuccessBanner(true);
      setTimeout(() => setSuccessBanner(false), 3000);
    } catch (e) {
      if (e instanceof ApiError) {
        setSaveError(`Errore nel salvataggio (${e.status}). Riprova.`);
      } else {
        setSaveError("Errore nel salvataggio. Riprova.");
      }
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <PraticheSkeleton />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!profilo) return null;

  const dirty = isDirty(form, original);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {successBanner && (
        <div className="bg-green-100 text-green-800 border border-green-200 px-4 py-3 rounded-lg text-sm font-medium">
          Profilo aggiornato con successo.
        </div>
      )}
      {saveError && (
        <div className="bg-red-100 text-red-800 border border-red-200 px-4 py-3 rounded-lg text-sm">
          {saveError}
        </div>
      )}

      {/* Dati identità — read-only */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <h2 className="text-lg font-semibold text-text mb-1">Dati identità</h2>
        <p className="text-xs text-muted-foreground mb-4">Per modificare questi dati contatta il supporto.</p>
        <div className="grid grid-cols-2 gap-3">
          <ReadOnlyField label="Nome" value={profilo.firstName} />
          <ReadOnlyField label="Cognome" value={profilo.lastName} />
          <ReadOnlyField label="Email" value={profilo.emailAddress} />
          <ReadOnlyField label="CF / P.IVA" value={profilo.codiceFiscale} />
        </div>
      </div>

      {/* Contatti — editable */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <h2 className="text-lg font-semibold text-text mb-4">Contatti</h2>
        <EditableField
          label="Telefono"
          value={form.phoneNumber}
          onChange={(v) => updateField("phoneNumber", v)}
        />
      </div>

      {/* Indirizzo — editable */}
      <div className="bg-card p-6 rounded-lg border border-border">
        <h2 className="text-lg font-semibold text-text mb-4">Indirizzo</h2>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <EditableField label="Via / Indirizzo" value={form.street} onChange={(v) => updateField("street", v)} />
          </div>
          <EditableField label="Interno" value={form.interno} onChange={(v) => updateField("interno", v)} />
          <EditableField label="Città" value={form.city} onChange={(v) => updateField("city", v)} />
          <EditableField label="Provincia" value={form.state} onChange={(v) => updateField("state", v)} />
          <EditableField label="CAP" value={form.postalCode} onChange={(v) => updateField("postalCode", v)} />
          <EditableField label="Paese" value={form.country} onChange={(v) => updateField("country", v)} />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="bg-brand text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-dark disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? "Salvataggio..." : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <input
        value={value}
        disabled
        readOnly
        className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none bg-surface text-muted-foreground"
      />
    </div>
  );
}

function EditableField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground block mb-1">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-border rounded-lg text-sm outline-none focus:border-brand bg-card text-text"
      />
    </div>
  );
}
